import './media/welcomeWidget.css.js';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { $, hide, append } from 'vscode/vscode/vs/base/browser/dom';
import { MarkdownString } from 'vscode/vscode/vs/base/common/htmlContent';
import { MarkdownRenderer } from 'vscode/vscode/vs/editor/browser/widget/markdownRenderer/browser/markdownRenderer';
import { ButtonBar } from 'vscode/vscode/vs/base/browser/ui/button/button';
import { mnemonicButtonLabel } from 'vscode/vscode/vs/base/common/labels';
import { defaultButtonStyles } from 'vscode/vscode/vs/platform/theme/browser/defaultStyles';
import { Action } from 'vscode/vscode/vs/base/common/actions';
import { ActionBar } from 'vscode/vscode/vs/base/browser/ui/actionbar/actionbar';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { ThemeIcon } from 'vscode/vscode/vs/base/common/themables';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { parseLinkedText } from 'vscode/vscode/vs/base/common/linkedText';
import { Link } from 'vscode/vscode/vs/platform/opener/browser/link';
import { renderLabelWithIcons } from 'vscode/vscode/vs/base/browser/ui/iconLabel/iconLabels';
import { renderFormattedText } from 'vscode/vscode/vs/base/browser/formattedTextRenderer';
import { registerThemingParticipant } from 'vscode/vscode/vs/platform/theme/common/themeService';
import 'vscode/vscode/vs/platform/theme/common/colorUtils';
import { contrastBorder } from 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import { editorWidgetBackground, widgetShadow, widgetBorder, editorWidgetForeground } from 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';

const _moduleId = "vs/workbench/contrib/welcomeDialog/browser/welcomeWidget";
class WelcomeWidget extends Disposable {
    constructor(_editor, instantiationService, commandService, telemetryService, openerService) {
        super();
        this._editor = _editor;
        this.instantiationService = instantiationService;
        this.commandService = commandService;
        this.telemetryService = telemetryService;
        this.openerService = openerService;
        this.markdownRenderer = this.instantiationService.createInstance(MarkdownRenderer, {});
        this._isVisible = false;
        this._rootDomNode = document.createElement('div');
        this._rootDomNode.className = 'welcome-widget';
        this.element = this._rootDomNode.appendChild($('.monaco-dialog-box'));
        this.element.setAttribute('role', 'dialog');
        hide(this._rootDomNode);
        this.messageContainer = this.element.appendChild($('.dialog-message-container'));
    }
    async executeCommand(commandId, ...args) {
        try {
            await this.commandService.executeCommand(commandId, ...args);
            this.telemetryService.publicLog2('workbenchActionExecuted', {
                id: commandId,
                from: 'welcomeWidget'
            });
        }
        catch (ex) {
        }
    }
    async render(title, message, buttonText, buttonAction) {
        if (!this._editor._getViewModel()) {
            return;
        }
        await this.buildWidgetContent(title, message, buttonText, buttonAction);
        this._editor.addOverlayWidget(this);
        this._show();
        this.telemetryService.publicLog2('workbenchActionExecuted', {
            id: 'welcomeWidgetRendered',
            from: 'welcomeWidget'
        });
    }
    async buildWidgetContent(title, message, buttonText, buttonAction) {
        const actionBar = this._register(( (new ActionBar(this.element, {}))));
        const action = this._register(( (new Action('dialog.close', ( localizeWithPath(_moduleId, 0, "Close Dialog")), ThemeIcon.asClassName(Codicon.dialogClose), true, async () => {
            this._hide();
        }))));
        actionBar.push(action, { icon: true, label: false });
        const renderBody = (message, icon) => {
            const mds = ( (new MarkdownString(undefined, { supportThemeIcons: true, supportHtml: true })));
            mds.appendMarkdown(`<a class="copilot">$(${icon})</a>`);
            mds.appendMarkdown(message);
            return mds;
        };
        const titleElement = this.messageContainer.appendChild($('#monaco-dialog-message-detail.dialog-message-detail-title'));
        const titleElementMdt = this.markdownRenderer.render(renderBody(title, 'zap'));
        titleElement.appendChild(titleElementMdt.element);
        this.buildStepMarkdownDescription(this.messageContainer, ( (message.split('\n').filter(x => x).map(text => parseLinkedText(text)))));
        const buttonsRowElement = this.messageContainer.appendChild($('.dialog-buttons-row'));
        const buttonContainer = buttonsRowElement.appendChild($('.dialog-buttons'));
        const buttonBar = this._register(( (new ButtonBar(buttonContainer))));
        const primaryButton = this._register(buttonBar.addButtonWithDescription({ title: true, secondary: false, ...defaultButtonStyles }));
        primaryButton.label = mnemonicButtonLabel(buttonText, true);
        this._register(primaryButton.onDidClick(async () => {
            await this.executeCommand(buttonAction);
        }));
        buttonBar.buttons[0].focus();
    }
    buildStepMarkdownDescription(container, text) {
        for (const linkedText of text) {
            const p = append(container, $('p'));
            for (const node of linkedText.nodes) {
                if (typeof node === 'string') {
                    const labelWithIcon = renderLabelWithIcons(node);
                    for (const element of labelWithIcon) {
                        if (typeof element === 'string') {
                            p.appendChild(renderFormattedText(element, { inline: true, renderCodeSegments: true }));
                        }
                        else {
                            p.appendChild(element);
                        }
                    }
                }
                else {
                    const link = this.instantiationService.createInstance(Link, p, node, {
                        opener: (href) => {
                            this.telemetryService.publicLog2('workbenchActionExecuted', {
                                id: 'welcomeWidetLinkAction',
                                from: 'welcomeWidget'
                            });
                            this.openerService.open(href, { allowCommands: true });
                        }
                    });
                    this._register(link);
                }
            }
        }
        return container;
    }
    getId() {
        return 'editor.contrib.welcomeWidget';
    }
    getDomNode() {
        return this._rootDomNode;
    }
    getPosition() {
        return {
            preference: 0
        };
    }
    _show() {
        if (this._isVisible) {
            return;
        }
        this._isVisible = true;
        this._rootDomNode.style.display = 'block';
    }
    _hide() {
        if (!this._isVisible) {
            return;
        }
        this._isVisible = true;
        this._rootDomNode.style.display = 'none';
        this._editor.removeOverlayWidget(this);
        this.telemetryService.publicLog2('workbenchActionExecuted', {
            id: 'welcomeWidgetDismissed',
            from: 'welcomeWidget'
        });
    }
}
registerThemingParticipant((theme, collector) => {
    const addBackgroundColorRule = (selector, color) => {
        if (color) {
            collector.addRule(`.monaco-editor ${selector} { background-color: ${color}; }`);
        }
    };
    const widgetBackground = theme.getColor(editorWidgetBackground);
    addBackgroundColorRule('.welcome-widget', widgetBackground);
    const widgetShadowColor = theme.getColor(widgetShadow);
    if (widgetShadowColor) {
        collector.addRule(`.welcome-widget { box-shadow: 0 0 8px 2px ${widgetShadowColor}; }`);
    }
    const widgetBorderColor = theme.getColor(widgetBorder);
    if (widgetBorderColor) {
        collector.addRule(`.welcome-widget { border-left: 1px solid ${widgetBorderColor}; border-right: 1px solid ${widgetBorderColor}; border-bottom: 1px solid ${widgetBorderColor}; }`);
    }
    const hcBorder = theme.getColor(contrastBorder);
    if (hcBorder) {
        collector.addRule(`.welcome-widget { border: 1px solid ${hcBorder}; }`);
    }
    const foreground = theme.getColor(editorWidgetForeground);
    if (foreground) {
        collector.addRule(`.welcome-widget { color: ${foreground}; }`);
    }
});

export { WelcomeWidget };
